import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminUsers } from '../../api/admin';
import { AdminDataTable, AdminSearchBar, AdminPagination, Column } from '../../components/admin';

const filterOptions = [
  { label: 'All Users', value: 'all' },
  { label: 'Experts', value: 'experts' },
  { label: 'Blacklisted', value: 'blacklisted' },
  { label: 'Subscribers', value: 'subscribers' },
];

const AdminUsers: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    fetchAdminUsers({ page, page_size: pageSize, search, filter })
      .then((res) => {
        setUsers(res.data.results);
        setTotal(res.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, filter]);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  const columns: Column[] = [
    { key: 'id', header: 'ID', width: '60px' },
    { key: 'name', header: 'Name', render: (row) => row.name || '-' },
    { key: 'email', header: 'Email', render: (row) => row.email || '-' },
    { key: 'phone_number', header: 'Phone', render: (row) => row.phone_number || '-' },
    {
      key: 'is_expert', header: 'Role', width: '100px',
      render: (row) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{
            backgroundColor: row.is_expert ? '#F59E0B20' : `${colors.primary}15`,
            color: row.is_expert ? '#F59E0B' : colors.primary,
          }}
        >
          {row.is_expert ? 'Expert' : 'User'}
        </span>
      ),
    },
    { key: 'subscription_tier', header: 'Tier', width: '80px' },
    {
      key: 'is_blacklisted', header: 'Status', width: '100px',
      render: (row) => row.is_blacklisted ? (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#EF444420', color: '#EF4444' }}>Blocked</span>
      ) : (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#10B98120', color: '#10B981' }}>Active</span>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>User Management</h1>

      <div className="mb-4">
        <AdminSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by name, email, or phone..."
          filterOptions={filterOptions}
          filterValue={filter}
          onFilterChange={setFilter}
        />
      </div>

      <AdminDataTable
        columns={columns}
        data={users}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        emptyMessage="No users found"
      />

      <AdminPagination
        page={page}
        totalPages={Math.ceil(total / pageSize)}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminUsers;
